import { useEffect, useState } from 'react';
import type { Either } from 'fp-ts/Either';
import type { TRPC } from '../../contexts/TRPC';
import { isLeftResult, isRightResult } from '../../lib/trpcTypeGuards';
import { captureAndReportError } from '../../utils/errorReporting';
import type {
  AdminReportingAutomatedReviewMetricsOutput,
  AdminReportingBackendIngestorMetricsOutput,
  AdminReportingOnboardingKybMetricsOutput,
  AdminReportingOverviewOutput,
  ReportingMetricAvailability,
  ReportingExternalMetrics,
} from './types';

interface UseReportingDataOptions {
  trpc: TRPC;
  dateFrom: string | null;
  dateTo: string | null;
  enabled: boolean;
}

type ReportingSectionKey =
  | 'store'
  | 'backendIngestor'
  | 'onboardingKyb'
  | 'automatedReview';

export type ReportingSectionStatus = 'idle' | 'loading' | 'loaded' | 'failed';
type SectionLoadingState = Record<ReportingSectionKey, boolean>;
type SectionStatusState = Record<ReportingSectionKey, ReportingSectionStatus>;

type ReportingQueryRange = {
  dateFrom: string;
  dateTo: string;
};

type ReportingSectionLoaderOptions<TOutput> = {
  key: ReportingSectionKey;
  operation: string;
  fallbackError: string;
  query: (queryRange: ReportingQueryRange) => Promise<Either<unknown, TOutput>>;
  onSuccess: (output: TOutput) => void;
};

const idleSectionStatus: SectionStatusState = {
  store: 'idle',
  backendIngestor: 'idle',
  onboardingKyb: 'idle',
  automatedReview: 'idle',
};

const loadingSectionStatus: SectionStatusState = {
  store: 'loading',
  backendIngestor: 'loading',
  onboardingKyb: 'loading',
  automatedReview: 'loading',
};

const toSectionLoading = (status: SectionStatusState): SectionLoadingState => ({
  store: status.store === 'loading',
  backendIngestor: status.backendIngestor === 'loading',
  onboardingKyb: status.onboardingKyb === 'loading',
  automatedReview: status.automatedReview === 'loading',
});

const defaultAvailability = (
  data: AdminReportingOverviewOutput | null,
  availability: ReportingMetricAvailability[],
): ReportingMetricAvailability[] =>
  availability.length > 0 ? availability : (data?.availability ?? []);

const mergeAvailability = (
  current: ReportingMetricAvailability[],
  next: ReportingMetricAvailability[],
) => {
  const byKey = new Map(current.map((item) => [item.key, item]));

  for (const item of next) {
    byKey.set(item.key, item);
  }

  const merged = current.map((item) => byKey.get(item.key) ?? item);
  const existingKeys = new Set(merged.map((item) => item.key));

  for (const item of next) {
    if (!existingKeys.has(item.key)) {
      merged.push(item);
    }
  }

  return merged;
};

const readLeftMessage = (result: unknown) => {
  const candidate = result as {
    _tag?: unknown;
    left?: { message?: unknown };
  };

  return candidate._tag === 'Left' &&
    typeof candidate.left?.message === 'string'
    ? candidate.left.message
    : null;
};

export function useReportingData({
  trpc,
  dateFrom,
  dateTo,
  enabled,
}: UseReportingDataOptions) {
  const [data, setData] = useState<AdminReportingOverviewOutput | null>(null);
  const [externalMetrics, setExternalMetrics] = useState<
    Partial<ReportingExternalMetrics>
  >({});
  const [availability, setAvailability] = useState<
    ReportingMetricAvailability[]
  >([]);
  const [sectionStatus, setSectionStatus] =
    useState<SectionStatusState>(idleSectionStatus);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sectionError, setSectionError] = useState<string | null>(null);

  useEffect(() => {
    let isCancelled = false;

    const load = async (): Promise<ReportingQueryRange | null> => {
      if (!enabled || !dateFrom || !dateTo) {
        setData(null);
        setExternalMetrics({});
        setAvailability([]);
        setSectionStatus(idleSectionStatus);
        setError(null);
        setSectionError(null);
        setIsLoading(false);
        return null;
      }

      const queryRange = { dateFrom, dateTo };

      setIsLoading(true);
      setError(null);
      setSectionError(null);
      setExternalMetrics({});
      setAvailability([]);
      setSectionStatus(loadingSectionStatus);

      try {
        const result = await trpc.client.getAdminReportingOverview.query({
          dateFrom: queryRange.dateFrom,
          dateTo: queryRange.dateTo,
        });

        if (isCancelled) {
          return null;
        }

        if (isRightResult(result)) {
          setData(result.right);
          setAvailability(result.right.availability);
          return queryRange;
        }

        if (isLeftResult(result)) {
          setData(null);
          setSectionStatus(idleSectionStatus);
          setError(result.left.message);
          return null;
        }
      } catch (loadError) {
        if (!isCancelled) {
          captureAndReportError(loadError, {
            operation: 'reporting.load',
            component: 'useReportingData',
          });
          setData(null);
          setSectionStatus(idleSectionStatus);
          setError('Failed to load reporting data');
        }
        return null;
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }

      return null;
    };

    const markSectionStatus = (
      key: ReportingSectionKey,
      value: ReportingSectionStatus,
    ) => {
      if (isCancelled) {
        return;
      }

      setSectionStatus((current) => ({
        ...current,
        [key]: value,
      }));
    };

    const loadReportingSection = async <TOutput>(
      queryRange: ReportingQueryRange,
      {
        key,
        operation,
        fallbackError,
        query,
        onSuccess,
      }: ReportingSectionLoaderOptions<TOutput>,
    ) => {
      let succeeded = false;
      markSectionStatus(key, 'loading');
      try {
        const result = await query(queryRange);

        if (isCancelled) {
          return;
        }

        if (isRightResult(result)) {
          onSuccess(result.right);
          succeeded = true;
          return;
        }

        const message = readLeftMessage(result);
        if (message) {
          setSectionError(message);
        }
      } catch (loadError) {
        if (!isCancelled) {
          captureAndReportError(loadError, {
            operation,
            component: 'useReportingData',
          });
          setSectionError(fallbackError);
        }
      } finally {
        markSectionStatus(key, succeeded ? 'loaded' : 'failed');
      }
    };

    const loadStore = (queryRange: ReportingQueryRange) =>
      loadReportingSection<AdminReportingOverviewOutput>(queryRange, {
        key: 'store',
        operation: 'reporting.store.load',
        fallbackError: 'Failed to load store reporting data',
        query: (range) =>
          trpc.client.getAdminReportingStoreMetrics.query({
            dateFrom: range.dateFrom,
            dateTo: range.dateTo,
          }),
        onSuccess: (output) => {
          setData(output);
          setAvailability((current) =>
            mergeAvailability(current, output.availability),
          );
        },
      });

    const loadBackendIngestor = async (queryRange: ReportingQueryRange) => {
      await loadReportingSection<AdminReportingBackendIngestorMetricsOutput>(
        queryRange,
        {
          key: 'backendIngestor',
          operation: 'reporting.backendIngestor.load',
          fallbackError: 'Failed to load backend ingestor reporting data',
          query: (range) =>
            trpc.client.getAdminReportingBackendIngestorMetrics.query({
              dateFrom: range.dateFrom,
              dateTo: range.dateTo,
            }),
          onSuccess: (output) => {
            setExternalMetrics((current) => ({
              ...current,
              backendIngestor: output.backendIngestor,
            }));
            setAvailability((current) =>
              mergeAvailability(current, output.availability),
            );
          },
        },
      );
    };

    const loadOnboarding = async (queryRange: ReportingQueryRange) => {
      await loadReportingSection<AdminReportingOnboardingKybMetricsOutput>(
        queryRange,
        {
          key: 'onboardingKyb',
          operation: 'reporting.onboardingKyb.load',
          fallbackError: 'Failed to load onboarding reporting data',
          query: (range) =>
            trpc.client.getAdminReportingOnboardingKybMetrics.query({
              dateFrom: range.dateFrom,
              dateTo: range.dateTo,
            }),
          onSuccess: (output) => {
            setExternalMetrics((current) => ({
              ...current,
              onboardingKyb: output.onboardingKyb,
            }));
            setAvailability((current) =>
              mergeAvailability(current, output.availability),
            );
          },
        },
      );
    };

    const loadAutomatedReview = async (queryRange: ReportingQueryRange) => {
      await loadReportingSection<AdminReportingAutomatedReviewMetricsOutput>(
        queryRange,
        {
          key: 'automatedReview',
          operation: 'reporting.automatedReview.load',
          fallbackError: 'Failed to load automated review reporting data',
          query: (range) =>
            trpc.client.getAdminReportingAutomatedReviewMetrics.query({
              dateFrom: range.dateFrom,
              dateTo: range.dateTo,
            }),
          onSuccess: (output) => {
            setExternalMetrics((current) => ({
              ...current,
              automatedReview: output.automatedReview,
            }));
            setAvailability((current) =>
              mergeAvailability(current, output.availability),
            );
          },
        },
      );
    };

    const loadAll = async () => {
      const queryRange = await load();

      if (isCancelled || !queryRange) {
        return;
      }

      void loadStore(queryRange);
      void loadBackendIngestor(queryRange);
      void loadOnboarding(queryRange);
      void loadAutomatedReview(queryRange);
    };

    void loadAll();

    return () => {
      isCancelled = true;
    };
  }, [dateFrom, dateTo, enabled, trpc]);

  return {
    data,
    externalMetrics,
    error,
    sectionError,
    isLoading,
    sectionLoading: toSectionLoading(sectionStatus),
    sectionStatus,
    availability: defaultAvailability(data, availability),
  };
}
